// Audio System - Handles all game audio including music, SFX, and ambient sounds

export type SoundCategory = 'master' | 'music' | 'sfx' | 'ambient' | 'voice' | 'ui';

export interface SoundEffect {
  id: string;
  url: string;
  category: SoundCategory;
  volume: number;
  loop: boolean;
  poolSize: number;
}

export interface AudioSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  ambientVolume: number;
  voiceVolume: number;
  uiVolume: number;
  muted: boolean;
}

interface SoundInstance {
  audio: HTMLAudioElement;
  inUse: boolean;
}

interface MusicTrack {
  id: string;
  audio: HTMLAudioElement;
  fadeInterval?: number;
}

export class AudioSystem {
  private context: AudioContext | null = null;
  private settings: AudioSettings;
  private soundPools: Map<string, SoundInstance[]> = new Map();
  private currentMusic: MusicTrack | null = null;
  private ambientSounds: Map<string, HTMLAudioElement> = new Map();
  private isInitialized: boolean = false;

  // Predefined sound effects (using Web Audio API synthesized sounds)
  private synthSounds: Map<string, () => void> = new Map();

  constructor() {
    this.settings = this.loadSettings();
    this.initializeSynthSounds();
  }

  private loadSettings(): AudioSettings {
    const saved = localStorage.getItem('laika_audio_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Use defaults
      }
    }

    return {
      masterVolume: 0.7,
      musicVolume: 0.5,
      sfxVolume: 0.8,
      ambientVolume: 0.4,
      voiceVolume: 1.0,
      uiVolume: 0.6,
      muted: false,
    };
  }

  public saveSettings(): void {
    localStorage.setItem('laika_audio_settings', JSON.stringify(this.settings));
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      this.context = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Resume context on user interaction (required by browsers)
      const resumeAudio = async () => {
        if (this.context?.state === 'suspended') {
          await this.context.resume();
        }
        document.removeEventListener('click', resumeAudio);
        document.removeEventListener('keydown', resumeAudio);
      };

      document.addEventListener('click', resumeAudio);
      document.addEventListener('keydown', resumeAudio);

      this.isInitialized = true;
      console.log('Audio system initialized');
    } catch (error) {
      console.warn('Failed to initialize audio system:', error);
    }
  }

  private initializeSynthSounds(): void {
    // UI Sounds
    this.synthSounds.set('ui_click', () => this.playSynthClick());
    this.synthSounds.set('ui_hover', () => this.playSynthHover());
    this.synthSounds.set('ui_open', () => this.playSynthOpen());
    this.synthSounds.set('ui_close', () => this.playSynthClose());
    this.synthSounds.set('ui_error', () => this.playSynthError());
    this.synthSounds.set('ui_success', () => this.playSynthSuccess());

    // Combat Sounds
    this.synthSounds.set('hit_physical', () => this.playSynthHit('physical'));
    this.synthSounds.set('hit_fire', () => this.playSynthHit('fire'));
    this.synthSounds.set('hit_ice', () => this.playSynthHit('ice'));
    this.synthSounds.set('hit_lightning', () => this.playSynthHit('lightning'));
    this.synthSounds.set('hit_arcane', () => this.playSynthHit('arcane'));
    this.synthSounds.set('hit_holy', () => this.playSynthHit('holy'));
    this.synthSounds.set('hit_shadow', () => this.playSynthHit('shadow'));

    // Skill Sounds
    this.synthSounds.set('skill_cast', () => this.playSynthCast());
    this.synthSounds.set('skill_ready', () => this.playSynthReady());
    this.synthSounds.set('buff_apply', () => this.playSynthBuff());
    this.synthSounds.set('debuff_apply', () => this.playSynthDebuff());
    this.synthSounds.set('heal', () => this.playSynthHeal());

    // World Sounds
    this.synthSounds.set('level_up', () => this.playSynthLevelUp());
    this.synthSounds.set('quest_complete', () => this.playSynthQuestComplete());
    this.synthSounds.set('item_pickup', () => this.playSynthPickup());
    this.synthSounds.set('gold_pickup', () => this.playSynthGold());
    this.synthSounds.set('equip', () => this.playSynthEquip());
    this.synthSounds.set('death', () => this.playSynthDeath());
    this.synthSounds.set('respawn', () => this.playSynthRespawn());

    // Chat Sounds
    this.synthSounds.set('chat_message', () => this.playSynthMessage());
    this.synthSounds.set('chat_whisper', () => this.playSynthWhisper());
  }

  // ============ Public API ============

  public play(soundId: string, volume: number = 1.0): void {
    if (this.settings.muted) return;

    const synthSound = this.synthSounds.get(soundId);
    if (synthSound) {
      synthSound();
      return;
    }

    // For file-based sounds (future)
    // this.playFromPool(soundId, volume);
  }

  public playUI(soundId: string): void {
    this.play(soundId, this.getVolume('ui'));
  }

  public playSFX(soundId: string): void {
    this.play(soundId, this.getVolume('sfx'));
  }

  public playMusic(trackId: string, fadeIn: boolean = true): void {
    // For now, we'll skip music as it requires actual audio files
    console.log(`Playing music: ${trackId}`);
  }

  public stopMusic(fadeOut: boolean = true): void {
    if (!this.currentMusic) return;

    if (fadeOut) {
      this.fadeOut(this.currentMusic.audio, 1000, () => {
        this.currentMusic?.audio.pause();
        this.currentMusic = null;
      });
    } else {
      this.currentMusic.audio.pause();
      this.currentMusic = null;
    }
  }

  public setVolume(category: SoundCategory, volume: number): void {
    volume = Math.max(0, Math.min(1, volume));

    switch (category) {
      case 'master':
        this.settings.masterVolume = volume;
        break;
      case 'music':
        this.settings.musicVolume = volume;
        if (this.currentMusic) {
          this.currentMusic.audio.volume = this.getVolume('music');
        }
        break;
      case 'sfx':
        this.settings.sfxVolume = volume;
        break;
      case 'ambient':
        this.settings.ambientVolume = volume;
        this.ambientSounds.forEach(sound => {
          sound.volume = this.getVolume('ambient');
        });
        break;
      case 'voice':
        this.settings.voiceVolume = volume;
        break;
      case 'ui':
        this.settings.uiVolume = volume;
        break;
    }

    this.saveSettings();
  }

  public getVolume(category: SoundCategory): number {
    const master = this.settings.masterVolume;
    switch (category) {
      case 'master':
        return master;
      case 'music':
        return master * this.settings.musicVolume;
      case 'sfx':
        return master * this.settings.sfxVolume;
      case 'ambient':
        return master * this.settings.ambientVolume;
      case 'voice':
        return master * this.settings.voiceVolume;
      case 'ui':
        return master * this.settings.uiVolume;
      default:
        return master;
    }
  }

  public mute(): void {
    this.settings.muted = true;
    this.saveSettings();
  }

  public unmute(): void {
    this.settings.muted = false;
    this.saveSettings();
  }

  public toggleMute(): void {
    this.settings.muted = !this.settings.muted;
    this.saveSettings();
  }

  public isMuted(): boolean {
    return this.settings.muted;
  }

  public getSettings(): AudioSettings {
    return { ...this.settings };
  }

  // ============ Synthesized Sounds ============

  private createOscillator(type: OscillatorType, frequency: number, duration: number, volume: number = 1): void {
    if (!this.context || this.settings.muted) return;

    try {
      const oscillator = this.context.createOscillator();
      const gainNode = this.context.createGain();

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, this.context.currentTime);

      const vol = volume * this.settings.masterVolume * this.settings.sfxVolume;
      gainNode.gain.setValueAtTime(vol * 0.3, this.context.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(this.context.destination);

      oscillator.start(this.context.currentTime);
      oscillator.stop(this.context.currentTime + duration);
    } catch (error) {
      // Silently fail if audio context is not ready
    }
  }

  private playNoise(duration: number, volume: number = 1): void {
    if (!this.context || this.settings.muted) return;

    try {
      const bufferSize = this.context.sampleRate * duration;
      const buffer = this.context.createBuffer(1, bufferSize, this.context.sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const source = this.context.createBufferSource();
      const gainNode = this.context.createGain();

      source.buffer = buffer;
      const vol = volume * this.settings.masterVolume * this.settings.sfxVolume * 0.2;
      gainNode.gain.setValueAtTime(vol, this.context.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + duration);

      source.connect(gainNode);
      gainNode.connect(this.context.destination);

      source.start();
    } catch (error) {
      // Silently fail
    }
  }

  // UI Sounds
  private playSynthClick(): void {
    this.createOscillator('sine', 800, 0.05, this.settings.uiVolume);
    this.createOscillator('sine', 1200, 0.03, this.settings.uiVolume * 0.5);
  }

  private playSynthHover(): void {
    this.createOscillator('sine', 600, 0.02, this.settings.uiVolume * 0.3);
  }

  private playSynthOpen(): void {
    this.createOscillator('sine', 400, 0.1, this.settings.uiVolume);
    setTimeout(() => this.createOscillator('sine', 600, 0.08, this.settings.uiVolume * 0.8), 50);
  }

  private playSynthClose(): void {
    this.createOscillator('sine', 600, 0.08, this.settings.uiVolume);
    setTimeout(() => this.createOscillator('sine', 400, 0.1, this.settings.uiVolume * 0.8), 30);
  }

  private playSynthError(): void {
    this.createOscillator('sawtooth', 200, 0.15, this.settings.uiVolume);
    setTimeout(() => this.createOscillator('sawtooth', 150, 0.2, this.settings.uiVolume), 100);
  }

  private playSynthSuccess(): void {
    this.createOscillator('sine', 523, 0.1, this.settings.uiVolume);
    setTimeout(() => this.createOscillator('sine', 659, 0.1, this.settings.uiVolume), 100);
    setTimeout(() => this.createOscillator('sine', 784, 0.15, this.settings.uiVolume), 200);
  }

  // Combat Sounds
  private playSynthHit(type: string): void {
    switch (type) {
      case 'physical':
        this.playNoise(0.08);
        this.createOscillator('sawtooth', 100, 0.1);
        break;
      case 'fire':
        this.playNoise(0.12);
        this.createOscillator('sawtooth', 300, 0.15);
        this.createOscillator('sine', 200, 0.1);
        break;
      case 'ice':
        this.createOscillator('triangle', 800, 0.1);
        this.createOscillator('sine', 1200, 0.08);
        break;
      case 'lightning':
        this.playNoise(0.05);
        this.createOscillator('square', 1000, 0.03);
        this.createOscillator('square', 2000, 0.02);
        break;
      case 'arcane':
        this.createOscillator('sine', 440, 0.15);
        this.createOscillator('sine', 880, 0.1);
        this.createOscillator('sine', 1320, 0.05);
        break;
      case 'holy':
        this.createOscillator('sine', 523, 0.2);
        this.createOscillator('sine', 659, 0.15);
        this.createOscillator('sine', 784, 0.1);
        break;
      case 'shadow':
        this.createOscillator('sawtooth', 150, 0.2);
        this.createOscillator('square', 100, 0.15);
        break;
    }
  }

  // Skill Sounds
  private playSynthCast(): void {
    this.createOscillator('sine', 300, 0.3);
    this.createOscillator('sine', 450, 0.25);
  }

  private playSynthReady(): void {
    this.createOscillator('sine', 600, 0.05);
  }

  private playSynthBuff(): void {
    this.createOscillator('sine', 400, 0.1);
    setTimeout(() => this.createOscillator('sine', 500, 0.1), 50);
    setTimeout(() => this.createOscillator('sine', 600, 0.15), 100);
  }

  private playSynthDebuff(): void {
    this.createOscillator('sawtooth', 300, 0.15);
    setTimeout(() => this.createOscillator('sawtooth', 200, 0.2), 100);
  }

  private playSynthHeal(): void {
    this.createOscillator('sine', 523, 0.1);
    setTimeout(() => this.createOscillator('sine', 659, 0.1), 80);
    setTimeout(() => this.createOscillator('sine', 784, 0.12), 160);
  }

  // World Sounds
  private playSynthLevelUp(): void {
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      setTimeout(() => this.createOscillator('sine', freq, 0.2), i * 100);
    });
  }

  private playSynthQuestComplete(): void {
    const notes = [392, 523, 659, 784];
    notes.forEach((freq, i) => {
      setTimeout(() => this.createOscillator('sine', freq, 0.15), i * 80);
    });
  }

  private playSynthPickup(): void {
    this.createOscillator('sine', 800, 0.05);
    setTimeout(() => this.createOscillator('sine', 1000, 0.05), 30);
  }

  private playSynthGold(): void {
    this.createOscillator('sine', 1200, 0.03);
    setTimeout(() => this.createOscillator('sine', 1400, 0.03), 20);
    setTimeout(() => this.createOscillator('sine', 1600, 0.03), 40);
  }

  private playSynthEquip(): void {
    this.playNoise(0.05);
    this.createOscillator('sine', 400, 0.08);
  }

  private playSynthDeath(): void {
    this.createOscillator('sawtooth', 200, 0.3);
    setTimeout(() => this.createOscillator('sawtooth', 150, 0.4), 150);
    setTimeout(() => this.createOscillator('sawtooth', 100, 0.5), 350);
  }

  private playSynthRespawn(): void {
    this.createOscillator('sine', 300, 0.2);
    setTimeout(() => this.createOscillator('sine', 400, 0.2), 150);
    setTimeout(() => this.createOscillator('sine', 523, 0.3), 300);
  }

  // Chat Sounds
  private playSynthMessage(): void {
    this.createOscillator('sine', 800, 0.03, this.settings.uiVolume * 0.5);
  }

  private playSynthWhisper(): void {
    this.createOscillator('sine', 600, 0.05, this.settings.uiVolume);
    setTimeout(() => this.createOscillator('sine', 800, 0.05, this.settings.uiVolume), 50);
  }

  // ============ Helpers ============

  private fadeOut(audio: HTMLAudioElement, duration: number, onComplete?: () => void): void {
    const startVolume = audio.volume;
    const step = startVolume / (duration / 50);

    const interval = setInterval(() => {
      audio.volume = Math.max(0, audio.volume - step);
      if (audio.volume <= 0) {
        clearInterval(interval);
        onComplete?.();
      }
    }, 50);
  }

  private fadeIn(audio: HTMLAudioElement, targetVolume: number, duration: number): void {
    audio.volume = 0;
    const step = targetVolume / (duration / 50);

    const interval = setInterval(() => {
      audio.volume = Math.min(targetVolume, audio.volume + step);
      if (audio.volume >= targetVolume) {
        clearInterval(interval);
      }
    }, 50);
  }

  public dispose(): void {
    this.stopMusic(false);
    this.ambientSounds.forEach(sound => sound.pause());
    this.ambientSounds.clear();
    this.context?.close();
  }
}

// Singleton instance
export const audioSystem = new AudioSystem();
